import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { io } from "socket.io-client";
import { useAuth } from "../Context/AuthContext";
import { API_URL } from "../config";

const ChatContext = createContext();

const makeDirectKey = (a, b) => {
  const s = String(a || "");
  const r = String(b || "");
  return s < r ? `${s}|${r}` : `${r}|${s}`;
};

const makeGroupKey = (groupId) => `group:${String(groupId)}`;

export const useChat = () => useContext(ChatContext);

export const ChatProvider = ({ children }) => {
  const { user, token } = useAuth();

  const socketBase = process.env.REACT_APP_SOCKET_URL || API_URL;
  const apiBase = process.env.REACT_APP_API_URL || API_URL;

  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);

  const [users, setUsers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [messagesByConversation, setMessagesByConversation] = useState({});
  const [typing, setTyping] = useState({});
  const [unread, setUnread] = useState({});
  const [groupNotices, setGroupNotices] = useState([]);
  const [toasts, setToasts] = useState([]);

  const [activeChat, setActiveChat] = useState(null); // {type: 'direct'|'group', id, name}
  const activeChatRef = useRef(null);
  const usersRef = useRef([]);
  const groupsRef = useRef([]);

  const audio = useRef(new Audio("/message-tone.wav"));

  const authHeaders = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const activeConversationKey = useMemo(() => {
    if (!activeChat || !user?._id) return "";
    if (activeChat.type === "group") return makeGroupKey(activeChat.id);
    return makeDirectKey(user._id, activeChat.id);
  }, [activeChat, user]);

  const activeMessages = activeConversationKey
    ? messagesByConversation[activeConversationKey] || []
    : [];

  const removeToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const pushToast = ({ title, message }) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    setToasts((prev) => [{ id, title, message }, ...prev].slice(0, 4));
    setTimeout(() => removeToast(id), 4500);
  };

  useEffect(() => {
    // Reset stale chat state on auth switch so old role data never flashes.
    setUsers([]);
    setGroups([]);
    setMessagesByConversation({});
    setTyping({});
    setUnread({});
    setGroupNotices([]);
    setActiveChat(null);
  }, [user?._id, token]);

  useEffect(() => {
    activeChatRef.current = activeChat;
  }, [activeChat]);

  useEffect(() => {
    usersRef.current = users;
  }, [users]);

  useEffect(() => {
    groupsRef.current = groups;
  }, [groups]);

  useEffect(() => {
    if (!user || !token) return;

    const s = io(socketBase, {
      auth: { token },
      transports: ["websocket"],
    });

    s.on("connect", () => {
      setConnected(true);
      s.emit("register", token);
    });

    s.on("disconnect", () => {
      setConnected(false);
    });

    s.on("message:new", (msg) => {
      const key = msg.conversationType === "group" && msg.groupId
        ? makeGroupKey(msg.groupId)
        : makeDirectKey(msg.senderId, msg.receiverId);

      setMessagesByConversation((prev) => ({
        ...prev,
        [key]: [...(prev[key] || []), msg],
      }));

      const isMyOwnMessage = String(msg.senderId) === String(user._id);
      if (isMyOwnMessage) return;

      const currentActive = activeChatRef.current;

      const isForMeDirect =
        msg.conversationType !== "group" &&
        String(msg.receiverId) === String(user._id) &&
        (!currentActive || String(currentActive.id) !== String(msg.senderId) || currentActive.type !== "direct");

      const isForMeGroup =
        msg.conversationType === "group" &&
        (!currentActive || currentActive.type !== "group" || String(currentActive.id) !== String(msg.groupId));

      if (isForMeDirect || isForMeGroup) {
        const unreadKey = msg.conversationType === "group" ? makeGroupKey(msg.groupId) : String(msg.senderId);
        setUnread((prev) => ({ ...prev, [unreadKey]: (prev[unreadKey] || 0) + 1 }));
        audio.current.play().catch(() => {});

        if (msg.conversationType === "group") {
          const group = groupsRef.current.find((g) => String(g._id) === String(msg.groupId));
          const sender = usersRef.current.find((u) => String(u._id) === String(msg.senderId));
          pushToast({
            title: group?.name ? `Group: ${group.name}` : "New group message",
            message: `${sender?.name || "Someone"}: ${msg.message || "Sent media"}`,
          });
        } else {
          const sender = usersRef.current.find((u) => String(u._id) === String(msg.senderId));
          pushToast({
            title: sender?.name || "New message",
            message: msg.message || "Sent media",
          });
        }
      }
    });

    s.on("chat:message_deleted", ({ messageId, groupId }) => {
      setMessagesByConversation((prev) => {
        const updated = { ...prev };
        const targetKeys = groupId ? [makeGroupKey(groupId)] : Object.keys(updated);
        targetKeys.forEach((key) => {
          updated[key] = (updated[key] || []).filter((m) => String(m._id) !== String(messageId));
        });
        return updated;
      });
    });

    s.on("presence:update", ({ userId, isOnline }) => {
      setUsers((prev) =>
        prev.map((u) => (String(u._id) === String(userId) ? { ...u, isOnline } : u))
      );
    });

    s.on("typing:start", ({ from, groupId }) => {
      const key = groupId ? makeGroupKey(groupId) : String(from);
      setTyping((prev) => ({ ...prev, [key]: true }));
    });

    s.on("typing:stop", ({ from, groupId }) => {
      const key = groupId ? makeGroupKey(groupId) : String(from);
      setTyping((prev) => ({ ...prev, [key]: false }));
    });

    s.on("chat:error", ({ message }) => {
      if (message) alert(message);
    });

    s.on("chat:read", ({ roomKey, messageIds = [], seenAt }) => {
      if (!roomKey) return;
      const idSet = new Set((messageIds || []).map(String));

      setMessagesByConversation((prev) => {
        const list = prev[roomKey] || [];
        if (!list.length) return prev;

        const updated = list.map((m) => {
          const matchesById = idSet.size > 0 && idSet.has(String(m._id));
          if (!matchesById) return m;
          return {
            ...m,
            read: true,
            deliveredAt: m.deliveredAt || seenAt || new Date().toISOString(),
            seenAt: m.seenAt || seenAt || new Date().toISOString(),
          };
        });
        return { ...prev, [roomKey]: updated };
      });
    });

    const syncPinnedState = (payload) => {
      const msg = payload?.chat;
      if (!msg?._id) return;

      setMessagesByConversation((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          const list = next[key] || [];
          if (!list.length) return;
          next[key] = list.map((m) =>
            String(m._id) === String(msg._id) ? { ...m, ...msg } : m
          );
        });
        return next;
      });
    };

    s.on("chat:message_pinned", syncPinnedState);
    s.on("chat:message_unpinned", syncPinnedState);

    s.on("chat:group_added", ({ group, addedBy }) => {
      if (!group?._id) return;

      setGroups((prev) => {
        if (prev.some((g) => String(g._id) === String(group._id))) return prev;
        return [group, ...prev];
      });

      setUnread((prev) => ({
        ...prev,
        [makeGroupKey(group._id)]: (prev[makeGroupKey(group._id)] || 0) + 1,
      }));

      setGroupNotices((prev) => [
        {
          id: `${group._id}-${Date.now()}`,
          text: `You were added to group "${group.name}" by ${addedBy?.name || "Admin"}.`,
          groupId: group._id,
        },
        ...prev,
      ]);

      pushToast({
        title: "Added To Group",
        message: `You were added to "${group.name}" by ${addedBy?.name || "Admin"}.`,
      });

      s.emit("join-chat-group", { groupId: group._id });
    });

    setSocket(s);
    return () => s.disconnect();
  }, [user, token, socketBase]);

  const fetchUsers = async () => {
    if (!user || !token) return;
    try {
      const res = await axios.get(`${apiBase}/api/chat/users`, {
        headers: authHeaders,
      });
      setUsers(res.data.users || []);
    } catch {
      setUsers([]);
    }
  };

  const fetchGroups = async () => {
    if (!user || !token) return;
    try {
      const res = await axios.get(`${apiBase}/api/chat/groups`, {
        headers: authHeaders,
      });
      setGroups(res.data.groups || []);
      if (socket && Array.isArray(res.data.groups)) {
        res.data.groups.forEach((group) => {
          socket.emit("join-chat-group", { groupId: group._id });
        });
      }
    } catch {
      setGroups([]);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const loadMessages = async (chat) => {
    if (!chat || !token) return;

    const key = chat.type === "group" ? makeGroupKey(chat.id) : makeDirectKey(user._id, chat.id);

    if (messagesByConversation[key]?.length) {
      return;
    }

    setLoading(true);
    try {
      const params = chat.type === "group" ? { groupId: chat.id } : { peerId: chat.id };
      const res = await axios.get(`${apiBase}/api/chat/messages`, {
        params,
        headers: authHeaders,
      });

      setMessagesByConversation((prev) => ({
        ...prev,
        [key]: res.data.messages || [],
      }));
    } catch (error) {
      console.error("loadMessages error", error);
    } finally {
      setLoading(false);
    }
  };

  const appendMessageLocally = (msg) => {
    if (!msg) return;
    const key =
      msg.conversationType === "group" && msg.groupId
        ? makeGroupKey(msg.groupId)
        : makeDirectKey(msg.senderId, msg.receiverId);

    setMessagesByConversation((prev) => {
      const existing = prev[key] || [];
      if (existing.some((item) => String(item._id) === String(msg._id))) {
        return prev;
      }
      return {
        ...prev,
        [key]: [...existing, msg],
      };
    });
  };

  const markConversationReadLocally = (chat) => {
    if (!chat || !user?._id) return;
    const key = chat.type === "group" ? makeGroupKey(chat.id) : makeDirectKey(user._id, chat.id);
    const nowIso = new Date().toISOString();

    setMessagesByConversation((prev) => {
      const list = prev[key] || [];
      if (!list.length) return prev;

      const updated = list.map((m) => {
        const isIncomingDirect =
          chat.type === "direct" &&
          String(m.receiverId) === String(user._id) &&
          String(m.senderId) === String(chat.id);

        const isIncomingGroup =
          chat.type === "group" &&
          String(m.groupId) === String(chat.id) &&
          String(m.senderId) !== String(user._id);

        if (isIncomingDirect || isIncomingGroup) {
          return { ...m, read: true, seenAt: m.seenAt || nowIso };
        }
        return m;
      });

      return { ...prev, [key]: updated };
    });
  };

  const openConversationWith = async (peerId, name = "") => {
    const chat = { type: "direct", id: String(peerId), name };
    setActiveChat(chat);
    setUnread((prev) => ({ ...prev, [String(peerId)]: 0 }));
    await loadMessages(chat);
    await markRead(chat);
    markConversationReadLocally(chat);
  };

  const openGroupConversation = async (group) => {
    const chat = { type: "group", id: String(group._id), name: group.name };
    setActiveChat(chat);
    setUnread((prev) => ({ ...prev, [makeGroupKey(group._id)]: 0 }));
    socket?.emit("join-chat-group", { groupId: group._id });
    await loadMessages(chat);
    await markRead(chat);
    markConversationReadLocally(chat);
  };

  const markRead = async (chat = activeChat) => {
    if (!chat || !token) return;
    try {
      const body = chat.type === "group" ? { groupId: chat.id } : { peerId: chat.id };
      await axios.post(`${apiBase}/api/chat/mark-read`, body, {
        headers: authHeaders,
      });
      markConversationReadLocally(chat);
    } catch (error) {
      console.error("markRead error", error);
    }
  };

  const sendMessageTo = async (targetId, text, type = "direct", options = {}) => {
    if (!text?.trim() || !token) return { success: false };

    const mentions =
      type === "group" && Array.isArray(options?.mentions) ? options.mentions : [];

    const body =
      type === "group"
        ? { groupId: targetId, message: text.trim(), mentions }
        : { receiverId: targetId, message: text.trim() };
    try {
      const res = await axios.post(`${apiBase}/api/chat/send`, body, {
        headers: authHeaders,
      });
      const chat = res?.data?.chat;
      appendMessageLocally(chat);
      return { success: true, chat };
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to send message";
      alert(message);
      return { success: false, message };
    }
  };

  const sendMessage = async (text, options = {}) => {
    if (!activeChat) return { success: false };
    return sendMessageTo(activeChat.id, text, activeChat.type, options);
  };

  const sendMedia = async (file, targetChat = activeChat) => {
    if (!file || !targetChat) return { success: false };

    const formData = new FormData();
    formData.append("message", file.name || "Media");
    formData.append("media", file);
    if (targetChat.type === "group") {
      formData.append("groupId", targetChat.id);
    } else {
      formData.append("receiverId", targetChat.id);
    }

    try {
      const res = await axios.post(`${apiBase}/api/chat/send`, formData, {
        headers: {
          ...authHeaders,
          "Content-Type": "multipart/form-data",
        },
      });
      const chat = res?.data?.chat;
      appendMessageLocally(chat);
      return { success: true, chat };
    } catch (error) {
      const message = error?.response?.data?.message || "Media upload failed";
      alert(message);
      return { success: false, message };
    }
  };

  const deleteMessage = async (messageId) => {
    if (!messageId || !token) return;
    await axios.delete(`${apiBase}/api/chat/messages/${messageId}`, {
      headers: authHeaders,
    });

    setMessagesByConversation((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((key) => {
        updated[key] = (updated[key] || []).filter((m) => String(m._id) !== String(messageId));
      });
      return updated;
    });

    socket?.emit("delete_message", { messageId });
  };

  const togglePinMessage = async (messageId, pin = true) => {
    if (!messageId || !token) {
      return { success: false, message: "Invalid pin request" };
    }

    try {
      let res;
      try {
        res = await axios.patch(
          `${apiBase}/api/chat/messages/${messageId}/pin`,
          { pin: Boolean(pin) },
          { headers: authHeaders }
        );
      } catch (patchErr) {
        // Fallback for environments where PATCH may be blocked/misrouted.
        res = await axios.post(
          `${apiBase}/api/chat/messages/${messageId}/pin`,
          { pin: Boolean(pin) },
          { headers: authHeaders }
        );
      }

      const chat = res?.data?.chat;
      if (chat?._id) {
        setMessagesByConversation((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((key) => {
            const list = next[key] || [];
            if (!list.length) return;
            next[key] = list.map((m) =>
              String(m._id) === String(chat._id) ? { ...m, ...chat } : m
            );
          });
          return next;
        });
      }

      return { success: true, chat };
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update pin";
      return { success: false, message };
    }
  };

  const forwardMessage = async ({ messageId, targetType, targetId }) => {
    if (!messageId || !targetId || !token) {
      return { success: false, message: "Invalid forward request" };
    }

    const body =
      targetType === "group"
        ? { messageId, groupId: targetId }
        : { messageId, receiverId: targetId };

    try {
      const res = await axios.post(`${apiBase}/api/chat/forward`, body, {
        headers: authHeaders,
      });
      const chat = res?.data?.chat;
      appendMessageLocally(chat);
      return { success: true, chat };
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to forward message";
      return { success: false, message };
    }
  };

  const downloadChatBackup = async (chat = activeChat, format = "json") => {
    if (!chat || !token) {
      return { success: false, message: "No active conversation selected" };
    }

    const params =
      chat.type === "group"
        ? { groupId: chat.id, format }
        : { peerId: chat.id, format };

    try {
      const response = await axios.get(`${apiBase}/api/chat/backup`, {
        params,
        headers: authHeaders,
        responseType: "blob",
      });

      const mimeType =
        format === "csv" ? "text/csv;charset=utf-8" : "application/json;charset=utf-8";
      const blob = new Blob([response.data], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      const chatName = String(chat.name || chat.id || "chat")
        .replace(/[^a-z0-9-_]+/gi, "-")
        .replace(/-+/g, "-")
        .toLowerCase();
      a.href = url;
      a.download = `chat-backup-${chatName}-${timestamp}.${format === "csv" ? "csv" : "json"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      return { success: true };
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to download backup";
      return { success: false, message };
    }
  };

  const createGroup = async ({ name, description = "", memberIds = [] }) => {
    const res = await axios.post(
      `${apiBase}/api/chat/groups`,
      { name, description, memberIds },
      { headers: authHeaders }
    );

    const group = res?.data?.group;
    if (group) {
      setGroups((prev) => [group, ...prev]);
      socket?.emit("join-chat-group", { groupId: group._id });
    }

    return group;
  };

  const addGroupMembers = async (groupId, memberIds = []) => {
    if (!groupId || !memberIds.length) return null;
    const res = await axios.patch(
      `${apiBase}/api/chat/groups/${groupId}/members`,
      { memberIds },
      { headers: authHeaders }
    );
    const updatedGroup = res?.data?.group;
    if (updatedGroup) {
      setGroups((prev) =>
        prev.map((g) => (String(g._id) === String(updatedGroup._id) ? updatedGroup : g))
      );
      socket?.emit("join-chat-group", { groupId: updatedGroup._id });
    }
    return updatedGroup;
  };

  const removeGroupMember = async (groupId, memberId) => {
    if (!groupId || !memberId) return null;
    const res = await axios.delete(
      `${apiBase}/api/chat/groups/${groupId}/members/${memberId}`,
      { headers: authHeaders }
    );
    const updatedGroup = res?.data?.group;
    if (updatedGroup) {
      setGroups((prev) =>
        prev.map((g) => (String(g._id) === String(updatedGroup._id) ? updatedGroup : g))
      );
    }
    return updatedGroup;
  };

  const leaveGroup = async (groupId) => {
    if (!groupId) return false;
    try {
      await axios.post(`${apiBase}/api/chat/groups/${groupId}/leave`, {}, { headers: authHeaders });
    } catch (error) {
      const status = Number(error?.response?.status || 0);

      // If group is already deleted/not found, clean local state and continue.
      if (status !== 404) {
        const message = error?.response?.data?.message || "Failed to leave group";
        alert(message);
        return false;
      }
    }

    setGroups((prev) => prev.filter((g) => String(g._id) !== String(groupId)));
    setMessagesByConversation((prev) => {
      const next = { ...prev };
      delete next[makeGroupKey(groupId)];
      return next;
    });
    if (activeChatRef.current?.type === "group" && String(activeChatRef.current.id) === String(groupId)) {
      setActiveChat(null);
    }
    return true;
  };

  const deleteGroup = async (groupId) => {
    if (!groupId) return false;
    await axios.delete(`${apiBase}/api/chat/groups/${groupId}`, { headers: authHeaders });
    setGroups((prev) => prev.filter((g) => String(g._id) !== String(groupId)));
    setMessagesByConversation((prev) => {
      const next = { ...prev };
      delete next[makeGroupKey(groupId)];
      return next;
    });
    setUnread((prev) => {
      const next = { ...prev };
      delete next[makeGroupKey(groupId)];
      return next;
    });
    if (activeChatRef.current?.type === "group" && String(activeChatRef.current?.id) === String(groupId)) {
      setActiveChat(null);
    }
    return true;
  };

  const startTyping = () => {
    if (!activeChat) return;
    if (activeChat.type === "group") {
      socket?.emit("typing:start", { groupId: activeChat.id });
      return;
    }
    socket?.emit("typing:start", { receiverId: activeChat.id });
  };

  const stopTyping = () => {
    if (!activeChat) return;
    if (activeChat.type === "group") {
      socket?.emit("typing:stop", { groupId: activeChat.id });
      return;
    }
    socket?.emit("typing:stop", { receiverId: activeChat.id });
  };

  useEffect(() => {
    if (!activeChat) return;
    const key = activeChat.type === "group" ? makeGroupKey(activeChat.id) : makeDirectKey(user?._id, activeChat.id);
    const list = messagesByConversation[key] || [];
    if (!list.length) return;

    const hasUnreadIncoming = list.some((m) => {
      if (activeChat.type === "group") {
        return String(m.groupId) === String(activeChat.id) && String(m.senderId) !== String(user?._id) && !m.read;
      }
      return (
        String(m.receiverId) === String(user?._id) &&
        String(m.senderId) === String(activeChat.id) &&
        !m.read
      );
    });

    if (hasUnreadIncoming) {
      markRead(activeChat);
    }
  }, [activeChat, messagesByConversation, user?._id]);

  const value = {
    socket,
    connected,
    socketReady: connected,
    loading,
    users,
    groups,
    messages: activeMessages,
    messagesByConversation,
    typing,
    unread,
    groupNotices,
    activeChat,
    activePeer: activeChat?.type === "direct" ? activeChat.id : null,
    currentConversation: activeConversationKey,
    me: {
      id: user?._id,
      role: String(user?.role || "").trim().toLowerCase(),
      name: user?.name,
    },

    fetchUsers,
    fetchGroups,
    openConversationWith,
    openGroupConversation,
    markRead,
    loadMessages,

    sendMessage,
    sendMessageTo,
    sendMedia,
    deleteMessage,
    togglePinMessage,
    forwardMessage,
    downloadChatBackup,
    createGroup,
    addGroupMembers,
    removeGroupMember,
    leaveGroup,
    deleteGroup,

    startTyping,
    stopTyping,
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
      <div
        style={{
          position: "fixed",
          right: 16,
          bottom: 16,
          zIndex: 100000,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          pointerEvents: "none",
          maxWidth: 340,
        }}
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            style={{
              pointerEvents: "auto",
              background: "#111827",
              color: "#fff",
              borderRadius: 10,
              boxShadow: "0 8px 20px rgba(0,0,0,0.25)",
              padding: "10px 12px",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <strong style={{ fontSize: 13, lineHeight: 1.2 }}>{toast.title}</strong>
              <button
                type="button"
                onClick={() => removeToast(toast.id)}
                style={{
                  border: 0,
                  background: "transparent",
                  color: "#cbd5e1",
                  cursor: "pointer",
                  fontSize: 14,
                  lineHeight: 1,
                }}
              >
                ×
              </button>
            </div>
            <div style={{ marginTop: 4, fontSize: 12, color: "#cbd5e1", overflowWrap: "anywhere" }}>
              {toast.message}
            </div>
          </div>
        ))}
      </div>
    </ChatContext.Provider>
  );
};
